import { Component } from '@theme/component';
import { getCustomSearchRedirectUrl } from '@theme/search-keyword-redirect';
import { debounce, onAnimationEnd, prefersReducedMotion } from '@theme/utilities';
import { sectionRenderer } from '@theme/section-renderer';
import { morph } from '@theme/morph';
import { RecentlyViewed } from '@theme/recently-viewed-products';
import { DialogCloseEvent, DialogOpenEvent } from '@theme/dialog';

/**
 * A custom element that allows the user to search for resources available on the store.
 *
 * @typedef {object} Refs
 * @property {HTMLInputElement} searchInput - The search input element.
 * @property {HTMLElement} predictiveSearchResults - The predictive search results container.
 * @property {HTMLElement} resetButton - The reset button element.
 * @property {HTMLElement[]} [resultsItems] - The search results items elements.
 * @property {HTMLElement} [recentlyViewedWrapper] - The recently viewed products wrapper.
 * @property {HTMLElement[]} [recentlyViewedTitle] - The recently viewed title elements.
 * @property {HTMLElement[]} [recentlyViewedItems] - The recently viewed product items.
 * @extends {Component<Refs>}
 */
class PredictiveSearchComponent extends Component {
  requiredRefs = ['searchInput', 'predictiveSearchResults', 'resetButton'];

  #controller = new AbortController();

  /**
   * @type {AbortController | null}
   */
  #activeFetch = null;

  #emptyStateLoaded = false;

  /** Header customizer headings — predictive-search-empty section defaults would override these on morph. */
  #applySearchModalHeadings(root) {
    const configEl = document.getElementById('predictive-search-trending-headings');
    if (!configEl || !configEl.textContent) return;

    let headings;
    try {
      headings = JSON.parse(configEl.textContent);
    } catch {
      return;
    }

    const scope = root instanceof HTMLElement ? root : document.getElementById('search-modal');
    if (!scope) return;

    const trendHeading = scope.querySelector(
      '.desktop_search_box .search_box_inner .para_pills_desktop'
    );
    const productsHeading = scope.querySelector(
      '.desktop_search_box .trending_products_section .trending_box .para_pills_desktop'
    );

    if (trendHeading && headings.trend_heading) {
      trendHeading.textContent = headings.trend_heading;
    }
    if (productsHeading && headings.trending_products_heading) {
      productsHeading.textContent = headings.trending_products_heading;
    }
  }

  get dialog() {
    return this.closest('dialog-component') ?? this.querySelector('dialog-component');
  }

  /** Header bar field (when using indirect sync from `search-modal-anchor.js`). */
  #getHeaderSlotInput() {
    const el = document.querySelector(
      '#header-component .header__search-slot [data-search-modal-anchor] input.search-input:not([type="hidden"])',
    );
    return el instanceof HTMLInputElement ? el : null;
  }

  /** Where focus should stay while searching from the header (`data-primary-search-input="header"`). */
  #getFocusInput() {
    if (this.dataset.primarySearchInput === 'header') {
      const h = this.#getHeaderSlotInput();
      if (h) return h;
    }
    return this.refs.searchInput;
  }

  /** Current query from the visible search field (header bar or modal input). */
  #getSearchQuery() {
    return (this.#getFocusInput()?.value ?? this.refs.searchInput.value).trim();
  }

  /**
   * @param {string} query
   */
  #navigateToSearchResults(query) {
    const searchUrl = new URL(Theme.routes.search_url, location.origin);
    searchUrl.searchParams.set('q', query);
    window.location.href = searchUrl.toString();
  }

  #refocusPrimaryInput() {
    if (this.dataset.primarySearchInput !== 'header') return;
    const el = this.#getFocusInput();
    if (el instanceof HTMLElement) {
      requestAnimationFrame(() => el.focus({ preventScroll: true }));
    }
  }

  connectedCallback() {
    super.connectedCallback();

    const { dialog } = this;
    const { signal } = this.#controller;

    if (this.refs.searchInput.value.length > 0) {
      this.#showResetButton();
    }

    if (dialog) {
      document.addEventListener('keydown', this.#handleKeyboardShortcut, { signal });
      dialog.addEventListener(DialogCloseEvent.eventName, this.#handleDialogClose, { signal });
      dialog.addEventListener(DialogOpenEvent.eventName, this.#handleDialogOpen, { signal });

      this.addEventListener('click', this.#handleModalClick, { signal });
    }
  }

  /**
   * Handles clicks within the predictive search modal to maintain focus on the input
   * @param {MouseEvent} event - The mouse event
   */
  #handleModalClick = (event) => {
    const target = /** @type {HTMLElement} */ (event.target);
    const isInteractiveElement =
      target instanceof HTMLButtonElement ||
      target instanceof HTMLAnchorElement ||
      target instanceof HTMLInputElement ||
      target.closest('button') ||
      target.closest('a') ||
      target.closest('input');

    const focusEl = this.#getFocusInput();
    if (!isInteractiveElement && focusEl) {
      focusEl.focus();
    }
  };

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#controller.abort();
  }

  /**
   * Handles the CMD+K key combination.
   * @param {KeyboardEvent} event - The keyboard event.
   */
  #handleKeyboardShortcut = (event) => {
    if (event.metaKey && event.key === 'k') {
      const d = /** @type {HTMLElement & { toggleDialog?: () => void }} */ (this.dialog);
      d?.toggleDialog?.();
    }
  };

  /**
   * Handles the dialog close event.
   * Restores the empty results panel but keeps the query so users can edit it on reopen.
   */
  #handleDialogClose = () => {
    void this.#resetSearch({ skipHeaderRefocus: true, preserveQuery: true });
  };

  #handleDialogOpen = () => {
    const headerIn = this.#getHeaderSlotInput();
    if (this.dataset.primarySearchInput === 'header' && headerIn?.value) {
      this.refs.searchInput.value = headerIn.value;
    }

    const query = this.#getSearchQuery();
    if (query.length > 0) {
      this.#showResetButton();
      this.#getSearchResults(query);
      return;
    }
    this.loadEmptyStateIfNeeded();
  };

  /**
   * Fetch and morph empty-state section HTML after user opens search (not on page load).
   * Skips network when inline Liquid empty state is enough (no recently viewed products).
   * @public
   */
  loadEmptyStateIfNeeded() {
    const query = this.#getSearchQuery();
    if (query.length > 0) {
      this.#showResetButton();
      this.#getSearchResults(query);
      return;
    }
    if (this.#emptyStateLoaded) return;
    if (RecentlyViewed.getProducts().length > 0) {
      this.#loadEmptyState();
      return;
    }
    this.#emptyStateLoaded = true;
  }

  #loadEmptyState() {
    if (this.#emptyStateLoaded) return;
    this.#emptyStateLoaded = true;
    // Keep any existing query (e.g. search results page) when hydrating empty-state chrome.
    void this.#resetSearch({ skipHeaderRefocus: true, preserveQuery: true });
  }

  get #allResultsItems() {
    const containers = Array.from(
      this.querySelectorAll(
        '.predictive-search-results__wrapper-queries, ' +
          '.predictive-search-results__wrapper-products, ' +
          '.predictive-search-results__list'
      )
    );

    const allItems = containers
      .flatMap((container) => {
        if (container.classList.contains('predictive-search-results__wrapper-products')) {
          return Array.from(container.querySelectorAll('.predictive-search-results__card'));
        }
        return Array.from(container.querySelectorAll('[ref="resultsItems[]"], .predictive-search-results__card'));
      })
      .filter((item) => item instanceof HTMLElement);

    return /** @type {HTMLElement[]} */ (allItems);
  }

  /**
   * Track whether the last interaction was keyboard-based
   * @type {boolean}
   */
  #isKeyboardNavigation = false;

  get #currentIndex() {
    return this.#allResultsItems?.findIndex((item) => item.getAttribute('aria-selected') === 'true') ?? -1;
  }

  set #currentIndex(index) {
    if (!this.#allResultsItems?.length) return;

    let activeItem = null;

    this.#allResultsItems.forEach((item) => {
      item.classList.remove('keyboard-focus');
    });

    for (const [itemIndex, item] of this.#allResultsItems.entries()) {
      if (itemIndex === index) {
        item.setAttribute('aria-selected', 'true');
        if (this.#isKeyboardNavigation) {
          item.classList.add('keyboard-focus');
        }
        activeItem = item;
      } else {
        item.removeAttribute('aria-selected');
      }
    }

    activeItem?.scrollIntoView({ behavior: prefersReducedMotion() ? 'instant' : 'smooth', block: 'nearest' });
    this.#getFocusInput()?.focus();
  }

  get #currentItem() {
    return this.#allResultsItems?.[this.#currentIndex];
  }

  /**
   * Redirects "Search for …" footer clicks when the query matches customizer keyword rules.
   * @param {SubmitEvent} event
   */
  onSearchSubmit = (event) => {
    const query = this.#getSearchQuery();
    const redirectUrl = getCustomSearchRedirectUrl(query);

    if (redirectUrl) {
      event.preventDefault();
      window.location.href = redirectUrl;
      return;
    }

    if (!query) {
      event.preventDefault();
    }
  };

  /**
   * Navigate through the predictive search results using arrow keys or close them with the Escape key.
   * @param {KeyboardEvent} event - The keyboard event.
   */
  onSearchKeyDown = (event) => {
    if (event.key === 'Escape') {
      // Let the dialog close; keep the query so it can be edited on reopen.
      return;
    }

    if (event.key === 'Enter') {
      const query = this.#getSearchQuery();
      const redirectUrl = getCustomSearchRedirectUrl(query);

      if (redirectUrl) {
        event.preventDefault();
        window.location.href = redirectUrl;
        return;
      }

      const singleResultContainer = this.refs.predictiveSearchResults.querySelector('[data-single-result-url]');
      if (singleResultContainer instanceof HTMLElement && singleResultContainer.dataset.singleResultUrl) {
        event.preventDefault();
        window.location.href = singleResultContainer.dataset.singleResultUrl;
        return;
      }

      if (this.#allResultsItems?.length && this.#currentIndex >= 0) {
        event.preventDefault();
        this.#currentItem?.querySelector('a')?.click();
        return;
      }

      if (query) {
        event.preventDefault();
        this.#navigateToSearchResults(query);
      }

      return;
    }

    if (!this.#allResultsItems?.length || event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
      return;
    }

    const currentIndex = this.#currentIndex;
    const totalItems = this.#allResultsItems.length;

    switch (event.key) {
      case 'ArrowDown':
        this.#isKeyboardNavigation = true;
        event.preventDefault();
        this.#currentIndex = currentIndex < totalItems - 1 ? currentIndex + 1 : 0;
        break;

      case 'Tab':
        if (event.shiftKey) {
          this.#isKeyboardNavigation = true;
          event.preventDefault();
          this.#currentIndex = currentIndex > 0 ? currentIndex - 1 : totalItems - 1;
        } else {
          this.#isKeyboardNavigation = true;
          event.preventDefault();
          this.#currentIndex = currentIndex < totalItems - 1 ? currentIndex + 1 : 0;
        }
        break;

      case 'ArrowUp':
        this.#isKeyboardNavigation = true;
        event.preventDefault();
        this.#currentIndex = currentIndex > 0 ? currentIndex - 1 : totalItems - 1;
        break;
    }
  };

  /**
   * Clears the recently viewed products.
   * @param {Event} event - The event.
   */
  clearRecentlyViewedProducts(event) {
    event.stopPropagation();

    RecentlyViewed.clearProducts();

    const { recentlyViewedItems, recentlyViewedTitle, recentlyViewedWrapper } = this.refs;

    const allRecentlyViewedElements = [...(recentlyViewedItems || []), ...(recentlyViewedTitle || [])];

    if (allRecentlyViewedElements.length === 0) {
      return;
    }

    if (recentlyViewedWrapper) {
      recentlyViewedWrapper.classList.add('removing');

      onAnimationEnd(recentlyViewedWrapper, () => {
        recentlyViewedWrapper.remove();
      });
    }
  }

  /**
   * Reset the search state.
   * @param {boolean} [keepFocus=true] - Whether to keep focus on input after reset
   */
  resetSearch = debounce((keepFocus = true) => {
    if (keepFocus) {
      this.#getFocusInput()?.focus();
    }
    void this.#resetSearch({ skipHeaderRefocus: !keepFocus });
  }, 100);

  /**
   * Debounce the search handler to fetch and display search results based on the input value.
   * Reset the current selection index and close results if the search term is empty.
   */
  search = debounce((event) => {
    // Real typing + programmatic sync (header → modal) both use InputEvent; ignore non-input events.
    if (!(event instanceof InputEvent)) return;

    const searchTerm = this.refs.searchInput.value.trim();
    this.#currentIndex = -1;

    if (!searchTerm.length) {
      this.#resetSearch();
      return;
    }

    this.#showResetButton();
    this.#getSearchResults(searchTerm);
  }, 200);

  /**
   * Resets scroll positions for search results containers
   */
  #resetScrollPositions() {
    requestAnimationFrame(() => {
      this.refs.predictiveSearchResults.querySelector('.predictive-search-results__inner')?.scrollTo(0, 0);
      this.querySelector('.predictive-search-form__content')?.scrollTo(0, 0);
    });
  }

  /**
   * Fetch search results using the section renderer and update the results container.
   * @param {string} searchTerm - The term to search for
   */
  async #getSearchResults(searchTerm) {
    if (!this.dataset.sectionId) return;

    const url = new URL(Theme.routes.predictive_search_url, location.origin);
    url.searchParams.set('q', searchTerm);
    url.searchParams.set('resources[type]', 'query,product');
    url.searchParams.set('resources[limit_scope]', 'each');

    const { predictiveSearchResults } = this.refs;

    const abortController = this.#createAbortController();

    sectionRenderer
      .getSectionHTML(this.dataset.sectionId, false, url)
      .then((resultsMarkup) => {
        if (!resultsMarkup) return;

        if (abortController.signal.aborted) return;

        morph(predictiveSearchResults, resultsMarkup);

        this.#resetScrollPositions();
        this.#refocusPrimaryInput();
      })
      .catch((error) => {
        if (abortController.signal.aborted) return;
        throw error;
      });
  }

  /**
   * Fetch the markup for the recently viewed products.
   * @returns {Promise<string | null>} The markup for the recently viewed products.
   */
  async #getRecentlyViewedProductsMarkup() {
    if (!this.dataset.sectionId) return null;

    const viewedProducts = RecentlyViewed.getProducts();
    if (viewedProducts.length === 0) return null;

    const url = new URL(Theme.routes.search_url, location.origin);
    url.searchParams.set('q', viewedProducts.map(/** @param {string} id */ (id) => `id:${id}`).join(' OR '));
    url.searchParams.set('resources[type]', 'product');

    return sectionRenderer.getSectionHTML(this.dataset.sectionId, false, url);
  }

  #hideResetButton() {
    const { resetButton } = this.refs;

    resetButton.hidden = true;
  }

  #showResetButton() {
    const { resetButton } = this.refs;

    resetButton.hidden = false;
  }

  #createAbortController() {
    const abortController = new AbortController();
    if (this.#activeFetch) {
      this.#activeFetch.abort();
    }
    this.#activeFetch = abortController;
    return abortController;
  }

  /**
   * Clear inputs and in-flight search immediately (no network).
   * @param {{ preserveQuery?: boolean }} [options]
   */
  #clearSearchFieldsSync(options = {}) {
    const { preserveQuery = false } = options;
    this.#currentIndex = -1;
    if (!preserveQuery) {
      this.refs.searchInput.value = '';
      const headerIn = this.#getHeaderSlotInput();
      if (this.dataset.primarySearchInput === 'header' && headerIn) {
        headerIn.value = '';
      }
      this.#hideResetButton();
    }
    if (this.#activeFetch) {
      this.#activeFetch.abort();
      this.#activeFetch = null;
    }
  }

  /**
   * @param {{ skipHeaderRefocus?: boolean; preserveQuery?: boolean }} [options]
   */
  #resetSearch = async (options = {}) => {
    const { skipHeaderRefocus = false, preserveQuery = false } = options;
    const { predictiveSearchResults } = this.refs;
    const emptySectionId = 'predictive-search-empty';

    this.#clearSearchFieldsSync({ preserveQuery });

    const abortController = this.#createAbortController();
    const url = new URL(window.location.href);
    url.searchParams.delete('page');

    const emptySectionMarkup = await sectionRenderer.getSectionHTML(emptySectionId, false, url);
    const parsedEmptySectionMarkup = new DOMParser()
      .parseFromString(emptySectionMarkup, 'text/html')
      .querySelector('.predictive-search-empty-section');

    if (!parsedEmptySectionMarkup) throw new Error('No empty section markup found');

    const viewedProducts = RecentlyViewed.getProducts();

    if (viewedProducts.length > 0) {
      try {
        const recentlyViewedMarkup = await this.#getRecentlyViewedProductsMarkup();
        if (recentlyViewedMarkup) {
          const parsedRecentlyViewedMarkup = new DOMParser().parseFromString(recentlyViewedMarkup, 'text/html');
          const recentlyViewedProductsHtml = parsedRecentlyViewedMarkup.getElementById('predictive-search-products');
          if (recentlyViewedProductsHtml) {
            for (const child of recentlyViewedProductsHtml.children) {
              if (child instanceof HTMLElement) {
                child.setAttribute('ref', 'recentlyViewedWrapper');
              }
            }
            const collectionElement = parsedEmptySectionMarkup.querySelector('#predictive-search-products');
            if (collectionElement) {
              collectionElement.prepend(...recentlyViewedProductsHtml.children);
            }
          }
        }
      } catch {
        /* Still morph base empty state below */
      }
    }

    if (abortController.signal.aborted) return;

    morph(predictiveSearchResults, parsedEmptySectionMarkup);
    this.#applySearchModalHeadings(predictiveSearchResults);
    this.#resetScrollPositions();
    window.dispatchEvent(
      new CustomEvent('predictive-search:empty-state-rendered', {
        bubbles: true,
        composed: true,
      })
    );
    if (!skipHeaderRefocus) {
      this.#refocusPrimaryInput();
    }
  };

  /**
   * Close the search modal. Keeps the current query so it can be edited on reopen.
   * Pass `{ clearQuery: true }` when the clear (×) control is used.
   * @param {{ clearQuery?: boolean }} [options]
   * @public
   */
  async closeSearchAndDialog(options = {}) {
    const clearQuery = options?.clearQuery === true || options instanceof Event;
    if (clearQuery) {
      this.#clearSearchFieldsSync();
    }
    this.#getHeaderSlotInput()?.blur();
    this.refs.searchInput?.blur();

    const modal = this.dialog;
    if (modal instanceof HTMLElement) {
      modal.setAttribute('data-open', 'false');
      document.querySelector('#header-component .header__search-slot')?.setAttribute('data-search-open', 'false');
    }

    const host = /** @type {HTMLElement & { closeDialog?: () => void }} */ (modal);
    await host?.closeDialog?.();
  }
}

if (!customElements.get('predictive-search-component')) {
  customElements.define('predictive-search-component', PredictiveSearchComponent);
}
